import React, { useEffect, useState } from "react";
import { CurrentCaseResponse, Guarantor, AddressRecord } from "../types";
import { api, ApiError } from "../api";
import { Alert, Spinner } from "../common/ui";
import { DocSlot } from "./DocSlot";

const STEPS = ["منبع اطلاعات", "اطلاعات کسب‌وکار", "محل فعالیت", "مدارک", "ضامن اول", "ضامن دوم", "بررسی و ثبت"];

export function Wizard({
  data, onRefresh, onExit,
}: {
  data: CurrentCaseResponse;
  onRefresh: () => Promise<void> | void;
  onExit: () => void;
}) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function withBusy(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "خطایی رخ داد");
      throw e;
    } finally {
      setBusy(false);
    }
  }

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => (step === 0 ? onExit() : setStep((s) => s - 1));

  return (
    <>
      <div style={{ padding: "10px 18px 0" }}>
        <div className="step-dots">
          {STEPS.map((_, i) => (
            <span key={i} className={`dot ${i === step ? "active" : i < step ? "done" : ""}`} />
          ))}
        </div>
        <div className="text-sm" style={{ textAlign: "center" }}>{STEPS[step]}</div>
      </div>
      <div className="wizard-body">
        {error && <Alert kind="error">{error}</Alert>}
        {step === 0 && <DataSourceStep data={data} onRefresh={onRefresh} withBusy={withBusy} />}
        {step === 1 && <ProfileStep data={data} withBusy={withBusy} />}
        {step === 2 && <LocationStep withBusy={withBusy} />}
        {step === 3 && <DocumentsStep data={data} onRefresh={onRefresh} />}
        {step === 4 && <GuarantorStep seq={1} withBusy={withBusy} onRefresh={onRefresh} data={data} />}
        {step === 5 && <GuarantorStep seq={2} withBusy={withBusy} onRefresh={onRefresh} data={data} />}
        {step === 6 && <ReviewStep data={data} onSubmitted={onExit} />}
      </div>
      <div className="wizard-footer">
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={prev} disabled={busy}>
          {step === 0 ? "بازگشت به داشبورد" : "مرحله قبل"}
        </button>
        {step < STEPS.length - 1 && (
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            disabled={busy}
            onClick={async () => {
              await onRefresh();
              next();
            }}
          >
            {busy ? <Spinner /> : "مرحله بعد"}
          </button>
        )}
      </div>
    </>
  );
}

// --- Step 1: data source ---
function DataSourceStep({ data, onRefresh, withBusy }: { data: CurrentCaseResponse; onRefresh: () => any; withBusy: (fn: () => Promise<void>) => Promise<void> }) {
  const [selected, setSelected] = useState<"ZOODEX" | "MANUAL" | null>((data.case.dataSource as any) || null);
  const [message, setMessage] = useState<string | null>(null);

  async function choose(source: "ZOODEX" | "MANUAL") {
    await withBusy(async () => {
      await api.setDataSource(source);
      setSelected(source);
      if (source === "ZOODEX") {
        const res = await api.zoodexPrefill();
        setMessage(res.applied ? "اطلاعات موجود در زودکس دریافت و اعمال شد." : res.message || "اطلاعاتی یافت نشد.");
      }
      await onRefresh();
    });
  }

  return (
    <div>
      <h3>اطلاعات کسب‌وکار خود را چگونه وارد می‌کنید؟</h3>
      <div className="card" style={{ cursor: "pointer", border: selected === "ZOODEX" ? "2px solid var(--raymand-indigo-deep)" : undefined }} onClick={() => choose("ZOODEX")}>
        <div className="card-title">دریافت اطلاعات از زودکس</div>
        <p className="mb-0">اطلاعات موجود در حساب زودکس شما دریافت شده و فقط موارد ناقص را تکمیل خواهید کرد.</p>
      </div>
      <div className="card" style={{ cursor: "pointer", border: selected === "MANUAL" ? "2px solid var(--raymand-indigo-deep)" : undefined }} onClick={() => choose("MANUAL")}>
        <div className="card-title">ورود اطلاعات به‌صورت دستی</div>
        <p className="mb-0">تمام اطلاعات کسب‌وکار را خودتان وارد می‌کنید.</p>
      </div>
      {message && <Alert kind="info">{message}</Alert>}
    </div>
  );
}

// --- Step 2: profile ---
function SourceTag({ field, sources }: { field: string; sources: Record<string, string> }) {
  const src = sources?.[field];
  if (!src) return null;
  return <span className={`source-tag ${src === "ZOODEX" ? "zoodex" : ""}`}>{src === "ZOODEX" ? "منبع: زودکس" : "ویرایش پذیرنده"}</span>;
}

function ProfileStep({ data, withBusy }: { data: CurrentCaseResponse; withBusy: (fn: () => Promise<void>) => Promise<void> }) {
  const [form, setForm] = useState(data.profile);
  const [amount, setAmount] = useState(String(data.case.requestedAmount || ""));
  const sources = data.profile.fieldSources || {};

  function field(name: keyof typeof form, label: string, type = "text") {
    return (
      <div className="field">
        <label>{label} <SourceTag field={name as string} sources={sources} /></label>
        <input
          type={type}
          value={(form as any)[name] || ""}
          onChange={(e: any) => setForm({ ...form, [name]: e.target.value })}
          onBlur={() => withBusy(() => api.updateProfile({ [name]: (form as any)[name] || "" }).then(() => {}))}
        />
      </div>
    );
  }

  return (
    <div>
      <h3>اطلاعات کسب‌وکار</h3>
      {field("storeName", "نام فروشگاه")}
      <div className="grid-2">
        {field("ownerFirstName", "نام صاحب جواز")}
        {field("ownerLastName", "نام خانوادگی صاحب جواز")}
      </div>
      <div className="grid-2">
        {field("ownerNationalId", "کد ملی صاحب جواز")}
        {field("ownerMobile", "شماره موبایل صاحب جواز", "tel")}
      </div>
      {field("businessCategory", "نوع فعالیت")}
      {field("postalCode", "کد پستی")}
      <div className="field">
        <label>مبلغ تسهیلات درخواستی (ریال)</label>
        <input
          type="number"
          value={amount}
          onChange={(e: any) => setAmount(e.target.value)}
          onBlur={() => withBusy(() => api.updateProfile({ requestedAmount: amount }).then(() => {}))}
        />
      </div>
      <div className="field">
        <label>توضیحات</label>
        <textarea rows={3} value={form.notes || ""} onChange={(e: any) => setForm({ ...form, notes: e.target.value })}
          onBlur={() => withBusy(() => api.updateProfile({ notes: form.notes || "" }).then(() => {}))} />
      </div>
    </div>
  );
}

// --- Step 3: location ---
function LocationStep({ withBusy }: { withBusy: (fn: () => Promise<void>) => Promise<void> }) {
  const [addr, setAddr] = useState<AddressRecord>({ entityType: "APPLICANT", line1: "", city: "", province: "", postalCode: "", propertyStatus: "RENTED" });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.getAddress("APPLICANT").then((r) => {
      if (r.found) setAddr(r.address);
      setLoaded(true);
    });
  }, []);

  async function save() {
    await withBusy(() => api.upsertAddress(addr).then(() => {}));
  }

  if (!loaded) return <Spinner />;

  return (
    <div>
      <h3>محل فعالیت</h3>
      <div className="field">
        <label>آدرس کامل محل فعالیت</label>
        <textarea rows={3} value={addr.line1} onChange={(e: any) => setAddr({ ...addr, line1: e.target.value })} onBlur={save} />
      </div>
      <div className="grid-2">
        <div className="field">
          <label>شهر</label>
          <input value={addr.city} onChange={(e: any) => setAddr({ ...addr, city: e.target.value })} onBlur={save} />
        </div>
        <div className="field">
          <label>استان</label>
          <input value={addr.province} onChange={(e: any) => setAddr({ ...addr, province: e.target.value })} onBlur={save} />
        </div>
      </div>
      <div className="field">
        <label>کد پستی</label>
        <input value={addr.postalCode} onChange={(e: any) => setAddr({ ...addr, postalCode: e.target.value })} onBlur={save} />
      </div>
      <div className="field">
        <label>وضعیت محل</label>
        <select value={addr.propertyStatus} onChange={(e: any) => { setAddr({ ...addr, propertyStatus: e.target.value }); }} onBlur={save}>
          <option value="RENTED">اجاره‌ای</option>
          <option value="OWNED">ملکی</option>
        </select>
      </div>
    </div>
  );
}

// --- Step 4: documents ---
function DocumentsStep({ data, onRefresh }: { data: CurrentCaseResponse; onRefresh: () => any }) {
  const docs = data.documents.filter((d) => d.entityType === "APPLICANT");
  const find = (type: string) => docs.find((d) => d.documentType === type);
  const hasLease = !!find("LEASE_AGREEMENT");
  const hasOwnership = !!find("OWNERSHIP_DOCUMENT");

  return (
    <div>
      <h3>مدارک متقاضی</h3>
      <div className="card-subtitle">هویتی</div>
      <DocSlot entityType="APPLICANT" documentType="NATIONAL_ID_FRONT" doc={find("NATIONAL_ID_FRONT")} onChanged={onRefresh} />
      <DocSlot entityType="APPLICANT" documentType="NATIONAL_ID_BACK" doc={find("NATIONAL_ID_BACK")} onChanged={onRefresh} />

      <div className="card-subtitle mt-3">صنفی</div>
      <DocSlot entityType="APPLICANT" documentType="BUSINESS_LICENSE" doc={find("BUSINESS_LICENSE")} onChanged={onRefresh} />
      <DocSlot entityType="APPLICANT" documentType="BUSINESS_LICENSE_SUPPLEMENT" doc={find("BUSINESS_LICENSE_SUPPLEMENT")} optional onChanged={onRefresh} />

      <div className="card-subtitle mt-3">محل فعالیت (یکی از این دو مورد کافی است)</div>
      {(!hasOwnership) && <DocSlot entityType="APPLICANT" documentType="LEASE_AGREEMENT" doc={find("LEASE_AGREEMENT")} onChanged={onRefresh} />}
      {(!hasLease) && <DocSlot entityType="APPLICANT" documentType="OWNERSHIP_DOCUMENT" doc={find("OWNERSHIP_DOCUMENT")} onChanged={onRefresh} />}

      <div className="card-subtitle mt-3">مالیاتی</div>
      <DocSlot entityType="APPLICANT" documentType="TAX_RETURN_YEAR1" doc={find("TAX_RETURN_YEAR1")} onChanged={onRefresh} />
      <DocSlot entityType="APPLICANT" documentType="TAX_RETURN_YEAR2" doc={find("TAX_RETURN_YEAR2")} onChanged={onRefresh} />
      <DocSlot entityType="APPLICANT" documentType="QUARTERLY_TAX_RETURN" doc={find("QUARTERLY_TAX_RETURN")} onChanged={onRefresh} />

      <div className="card-subtitle mt-3">مالی</div>
      <DocSlot entityType="APPLICANT" documentType="SALES_FORECAST" doc={find("SALES_FORECAST")} onChanged={onRefresh} />
    </div>
  );
}

// --- Step 5/6: guarantor ---
function GuarantorStep({ seq, withBusy, onRefresh, data }: { seq: 1 | 2; withBusy: (fn: () => Promise<void>) => Promise<void>; onRefresh: () => any; data: CurrentCaseResponse }) {
  const existing = data.guarantors.find((g) => g.seq === seq);
  const [form, setForm] = useState<Partial<Guarantor>>(existing || { firstName: "", lastName: "", nationalId: "", mobile: "", businessName: "", businessActivity: "", businessAddress: "" });
  const [addr, setAddr] = useState<AddressRecord>({ entityType: "GUARANTOR", entityId: existing?.id, line1: "", city: "", province: "", postalCode: "", propertyStatus: "OWNED" });
  const [guarantorId, setGuarantorId] = useState<string | undefined>(existing?.id);

  useEffect(() => {
    if (guarantorId) {
      api.getAddress("GUARANTOR", guarantorId).then((r) => {
        if (r.found) setAddr(r.address);
      });
    }
  }, [guarantorId]);

  async function saveProfile() {
    await withBusy(async () => {
      const saved = await api.upsertGuarantor(seq, form);
      setGuarantorId(saved.id);
      await onRefresh();
    });
  }

  async function saveAddress() {
    if (!guarantorId) return;
    await withBusy(() => api.upsertAddress({ ...addr, entityType: "GUARANTOR", entityId: guarantorId }).then(() => {}));
  }

  const docs = data.documents.filter((d) => d.entityType === "GUARANTOR" && d.entityId === guarantorId);
  const find = (type: string) => docs.find((d) => d.documentType === type);

  return (
    <div>
      <h3>ضامن {seq === 1 ? "اول" : "دوم"}</h3>
      <div className="grid-2">
        <div className="field"><label>نام</label><input value={form.firstName || ""} onChange={(e: any) => setForm({ ...form, firstName: e.target.value })} onBlur={saveProfile} /></div>
        <div className="field"><label>نام خانوادگی</label><input value={form.lastName || ""} onChange={(e: any) => setForm({ ...form, lastName: e.target.value })} onBlur={saveProfile} /></div>
      </div>
      <div className="grid-2">
        <div className="field"><label>کد ملی</label><input value={form.nationalId || ""} onChange={(e: any) => setForm({ ...form, nationalId: e.target.value })} onBlur={saveProfile} /></div>
        <div className="field"><label>شماره موبایل</label><input value={form.mobile || ""} onChange={(e: any) => setForm({ ...form, mobile: e.target.value })} onBlur={saveProfile} /></div>
      </div>
      <div className="field"><label>نام واحد صنفی</label><input value={form.businessName || ""} onChange={(e: any) => setForm({ ...form, businessName: e.target.value })} onBlur={saveProfile} /></div>
      <div className="field"><label>نوع فعالیت</label><input value={form.businessActivity || ""} onChange={(e: any) => setForm({ ...form, businessActivity: e.target.value })} onBlur={saveProfile} /></div>
      <div className="field"><label>آدرس کسب‌وکار</label><input value={form.businessAddress || ""} onChange={(e: any) => setForm({ ...form, businessAddress: e.target.value })} onBlur={saveProfile} /></div>

      {!guarantorId && <Alert kind="info">پس از تکمیل اطلاعات بالا (خروج از هر فیلد) می‌توانید مدارک و آدرس را وارد کنید.</Alert>}

      {guarantorId && (
        <>
          <div className="card-subtitle mt-3">آدرس کسب‌وکار (برای Address Matching)</div>
          <div className="field">
            <textarea rows={2} value={addr.line1} onChange={(e: any) => setAddr({ ...addr, line1: e.target.value })} onBlur={saveAddress} placeholder="آدرس دقیق" />
          </div>

          <div className="card-subtitle mt-3">مدارک ضامن</div>
          <DocSlot entityType="GUARANTOR" entityId={guarantorId} documentType="NATIONAL_ID_FRONT" doc={find("NATIONAL_ID_FRONT")} onChanged={onRefresh} />
          <DocSlot entityType="GUARANTOR" entityId={guarantorId} documentType="NATIONAL_ID_BACK" doc={find("NATIONAL_ID_BACK")} onChanged={onRefresh} />
          <DocSlot entityType="GUARANTOR" entityId={guarantorId} documentType="BUSINESS_LICENSE" doc={find("BUSINESS_LICENSE")} onChanged={onRefresh} />
          <DocSlot entityType="GUARANTOR" entityId={guarantorId} documentType="GUARANTOR_TAX_RETURN" doc={find("GUARANTOR_TAX_RETURN")} onChanged={onRefresh} />
        </>
      )}
    </div>
  );
}

// --- Step 7: review + submit ---
function ReviewStep({ data, onSubmitted }: { data: CurrentCaseResponse; onSubmitted: () => void }) {
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const { completeness } = data;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.submit(["ZOODEX_DATA_SHARE", "TERMS", "CREDIT_INQUIRY"]);
      setDone(true);
      setTimeout(onSubmitted, 1200);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return <Alert kind="success">پرونده شما با موفقیت ثبت شد. در حال انتقال به داشبورد...</Alert>;
  }

  return (
    <div>
      <h3>بررسی نهایی</h3>
      {completeness.sections.map((s) => (
        <div className="checklist-item" key={s.key}>
          <span className={`checklist-icon ${s.complete ? "ok" : "warn"}`}>{s.complete ? "✓" : "!"}</span>
          <div>
            <div>{s.label}</div>
            {!s.complete && s.detail && <div className="text-sm">{s.detail}</div>}
          </div>
        </div>
      ))}

      <div className="card mt-3">
        <label className="flex gap-2" style={{ alignItems: "flex-start", marginBottom: 10 }}>
          <input type="checkbox" checked={consent1} onChange={(e: any) => setConsent1(e.target.checked)} style={{ width: "auto", marginTop: 3 }} />
          <span className="text-sm">با اشتراک‌گذاری اطلاعات کسب‌وکار من میان زودکس، رایمند و بانک صادرات برای بررسی این درخواست موافقم.</span>
        </label>
        <label className="flex gap-2" style={{ alignItems: "flex-start" }}>
          <input type="checkbox" checked={consent2} onChange={(e: any) => setConsent2(e.target.checked)} style={{ width: "auto", marginTop: 3 }} />
          <span className="text-sm">صحت اطلاعات و مدارک ارسالی را تأیید می‌کنم و با استعلام اعتباری موافقم.</span>
        </label>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      <button className="btn btn-primary" disabled={!completeness.canSubmit || !consent1 || !consent2 || busy} onClick={submit}>
        {busy ? <Spinner /> : completeness.canSubmit ? "ثبت نهایی درخواست" : "ابتدا موارد ناقص را تکمیل کنید"}
      </button>
    </div>
  );
}
